(function () {
	let runToken = 0;
	let debounceTimer = null;

	function parseTargetRgb(input) {
		const s = String(input || "").trim();
		const rgba = s.match(
			/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i,
		);
		if (rgba) {
			return {
				r: Number(rgba[1]),
				g: Number(rgba[2]),
				b: Number(rgba[3]),
			};
		}
		const hex = s.replace("#", "").trim();
		if (hex.length === 3) {
			return {
				r: parseInt(hex[0] + hex[0], 16),
				g: parseInt(hex[1] + hex[1], 16),
				b: parseInt(hex[2] + hex[2], 16),
			};
		}
		if (hex.length === 6) {
			return {
				r: parseInt(hex.slice(0, 2), 16),
				g: parseInt(hex.slice(2, 4), 16),
				b: parseInt(hex.slice(4, 6), 16),
			};
		}
		return { r: 255, g: 0, b: 255 };
	}

	function mapPosToken(t, axis) {
		if (t === "center") return "50%";
		if (axis === "x") {
			if (t === "left") return "0%";
			if (t === "right") return "100%";
		} else {
			if (t === "top") return "0%";
			if (t === "bottom") return "100%";
		}
		return t;
	}

	function parseObjectPositionOffsets(posStr, cw, ch, rw, rh) {
		const parts = posStr.trim().split(/\s+/).filter(Boolean);
		let tx = parts[0] || "50%";
		let ty = parts[1];
		if (parts.length === 1) {
			if (["top", "bottom"].includes(tx)) {
				ty = tx;
				tx = "50%";
			} else if (["left", "right"].includes(tx)) {
				ty = "50%";
			} else if (tx === "center") {
				tx = "50%";
				ty = "50%";
			} else {
				ty = "50%";
			}
		}
		tx = mapPosToken(tx, "x");
		ty = mapPosToken(ty, "y");
		function toOff(token, c, r) {
			const s = String(token);
			if (s.endsWith("%")) {
				const p = parseFloat(s) / 100;
				return p * (c - r);
			}
			if (s.endsWith("px")) return parseFloat(s);
			return (c - r) / 2;
		}
		return {
			offX: toOff(tx, cw, rw),
			offY: toOff(ty, ch, rh),
		};
	}

	function naturalToDisplayPoint(nx, ny, nw, nh, cw, ch, imgEl) {
		const style = getComputedStyle(imgEl);
		const objectFit = style.objectFit || "fill";
		const posStr = style.objectPosition || "50% 50%";

		if (objectFit === "fill") {
			return {
				x: (nx / nw) * cw,
				y: (ny / nh) * ch,
			};
		}

		let scale;
		if (objectFit === "contain" || objectFit === "scale-down") {
			scale = Math.min(cw / nw, ch / nh);
		} else if (objectFit === "none") {
			scale = 1;
		} else {
			scale = Math.max(cw / nw, ch / nh);
		}

		const rw = nw * scale;
		const rh = nh * scale;
		const { offX, offY } = parseObjectPositionOffsets(
			posStr,
			cw,
			ch,
			rw,
			rh,
		);
		return {
			x: offX + nx * scale,
			y: offY + ny * scale,
		};
	}

	function applyPinDotCentered(markerEl, pxX, pxY, boxEl, fineOffsetX, fineOffsetY) {
		markerEl.classList.remove("left", "right", "top", "bottom");
		markerEl.style.right = "";
		markerEl.style.bottom = "";
		markerEl.style.transition = "none";
		markerEl.classList.add("product-markers__marker--dot-anchor");

		const boxW = boxEl.clientWidth || 1;
		const boxH = boxEl.clientHeight || 1;
		const pctX = (pxX / boxW) * 100;
		const pctY = (pxY / boxH) * 100;
		markerEl.classList.toggle("product-markers__card-left", pctX >= 75);
		markerEl.classList.toggle("product-markers__card-top", pctY <= 25);

		const countEl = markerEl.querySelector(":scope > .product-markers__count");
		if (countEl) {
			countEl.style.left = "0";
			countEl.style.top = "0";
			countEl.style.right = "auto";
			countEl.style.bottom = "auto";
			countEl.style.margin = "0";
			countEl.style.transform = "translate(-50%, -50%)";
			countEl.style.transition = "none";
		}

		markerEl.style.left = `${pxX + fineOffsetX}px`;
		markerEl.style.top = `${pxY + fineOffsetY}px`;
	}

	function loadImageCors(src) {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = "anonymous";
			img.onload = () => resolve(img);
			img.onerror = () => reject(new Error("img load"));
			img.src = src;
		});
	}

	async function loadImageViaFetch(url) {
		const res = await fetch(url, { mode: "cors", credentials: "omit" });
		if (!res.ok) throw new Error("fetch");
		const blob = await res.blob();
		const burl = URL.createObjectURL(blob);
		try {
			return await loadImageCors(burl);
		} finally {
			URL.revokeObjectURL(burl);
		}
	}

	async function loadScanImage(scanSrc) {
		try {
			return await loadImageCors(scanSrc);
		} catch {
			return loadImageViaFetch(scanSrc);
		}
	}

	function sampleImageToCanvas(imgEl, maxSide) {
		const nw = imgEl.naturalWidth;
		const nh = imgEl.naturalHeight;
		if (!nw || !nh) return null;

		let tw = nw;
		let th = nh;
		if (Math.max(tw, th) > maxSide) {
			const r = maxSide / Math.max(tw, th);
			tw = Math.round(nw * r);
			th = Math.round(nh * r);
		}

		const canvas = document.createElement("canvas");
		canvas.width = tw;
		canvas.height = th;
		const ctx = canvas.getContext("2d", { willReadFrequently: true });
		ctx.drawImage(imgEl, 0, 0, tw, th);

		let raw;
		try {
			raw = ctx.getImageData(0, 0, tw, th).data;
		} catch {
			return null;
		}

		return { tw, th, nw, nh, raw };
	}

	function findClusters(tw, th, raw, rgb, minArea) {
		const perChannelTol = 70;
		const distSqMax = 95 * 95;

		function matches(linear) {
			const o = linear * 4;
			if (raw[o + 3] < 32) return false;
			const r = raw[o];
			const g = raw[o + 1];
			const b = raw[o + 2];
			if (Math.abs(r - rgb.r) > perChannelTol) return false;
			if (Math.abs(g - rgb.g) > perChannelTol) return false;
			if (Math.abs(b - rgb.b) > perChannelTol) return false;
			const dr = r - rgb.r;
			const dg = g - rgb.g;
			const db = b - rgb.b;
			return dr * dr + dg * dg + db * db <= distSqMax;
		}

		const vis = new Uint8Array(tw * th);
		const clusters = [];

		for (let y = 0; y < th; y++) {
			for (let x = 0; x < tw; x++) {
				const start = y * tw + x;
				if (vis[start]) continue;
				if (!matches(start)) continue;

				const stack = [[x, y]];
				let sumX = 0;
				let sumY = 0;
				let n = 0;

				while (stack.length) {
					const [cx, cy] = stack.pop();
					const ci = cy * tw + cx;
					if (vis[ci]) continue;
					if (!matches(ci)) continue;
					vis[ci] = 1;
					sumX += cx;
					sumY += cy;
					n += 1;
					[
						[1, 0],
						[-1, 0],
						[0, 1],
						[0, -1],
					].forEach(([dx, dy]) => {
						const nx = cx + dx;
						const ny = cy + dy;
						if (nx >= 0 && nx < tw && ny >= 0 && ny < th)
							stack.push([nx, ny]);
					});
				}

				if (n >= minArea) {
					clusters.push({ cx: sumX / n, cy: sumY / n, area: n });
				}
			}
		}

		return clusters;
	}

	function pickAndOrderClusters(clusters, maxN) {
		if (!clusters.length) return [];
		const byArea = clusters.slice().sort((a, b) => b.area - a.area);
		const cut = Math.min(byArea.length, Math.max(maxN, 1));
		const top = byArea.slice(0, cut);
		top.sort((a, b) => a.cy - b.cy || a.cx - b.cx);
		return top;
	}

	async function detectNaturalClusters(scanSrc, rgb, markerCount) {
		const sourceImg = await loadScanImage(scanSrc);
		const sample = sampleImageToCanvas(sourceImg, 2400);
		if (!sample) return [];

		const { tw, th, nw, nh, raw } = sample;
		const minArea = Math.max(20, Math.round((tw * th) / 50000));
		const canvasClusters = findClusters(tw, th, raw, rgb, minArea);
		const naturalClusters = canvasClusters.map((c) => ({
			rx: c.cx / tw,
			ry: c.cy / th,
			area: c.area,
		}));

		return pickAndOrderClusters(naturalClusters, markerCount);
	}

	function waitForStableLayout(boxEl, imgEl, stableMs = 200) {
		return new Promise((resolve) => {
			let lastKey = "";
			let stableTimer = null;
			let fallbackTimer = null;
			let ro = null;

			const finish = () => {
				if (stableTimer) clearTimeout(stableTimer);
				if (fallbackTimer) clearTimeout(fallbackTimer);
				if (ro) ro.disconnect();
				resolve();
			};

			const measure = () => {
				if (!imgEl.complete || !imgEl.naturalWidth) return;
				const boxW = boxEl.clientWidth;
				const boxH = boxEl.clientHeight;
				const imgR = imgEl.getBoundingClientRect();
				if (boxW < 4 || boxH < 4 || imgR.width < 4 || imgR.height < 4) return;

				const key = [
					boxW,
					boxH,
					Math.round(imgR.width * 10),
					Math.round(imgR.height * 10),
					imgEl.naturalWidth,
					imgEl.naturalHeight,
				].join(":");

				if (key === lastKey) return;
				lastKey = key;
				clearTimeout(stableTimer);
				stableTimer = setTimeout(finish, stableMs);
			};

			ro = new ResizeObserver(measure);
			ro.observe(boxEl);
			ro.observe(imgEl);
			if (imgEl.complete) measure();
			else imgEl.addEventListener("load", measure, { once: true });
			fallbackTimer = setTimeout(finish, 4000);
		});
	}

	function readCachedNaturalClusters(overlay) {
		const raw = overlay.dataset.dotNaturalClusters;
		if (!raw) return null;
		try {
			const parsed = JSON.parse(raw);
			return Array.isArray(parsed) ? parsed : null;
		} catch {
			return null;
		}
	}

	function writeCachedNaturalClusters(overlay, clusters) {
		overlay.dataset.dotNaturalClusters = JSON.stringify(
			clusters.map((c) => ({ rx: c.rx, ry: c.ry })),
		);
	}

	function sortMarkersByCollectionOrder(markers, overlay) {
		const orderStr = overlay?.dataset.roomProductOrder;
		if (!orderStr) {
			return markers
				.slice()
				.sort(
					(a, b) =>
						Number(a.dataset.index || 0) - Number(b.dataset.index || 0),
				);
		}
		const order = orderStr.split(",").map((s) => s.trim()).filter(Boolean);
		return markers.slice().sort((a, b) => {
			const ai = order.indexOf(String(a.dataset.productId || ""));
			const bi = order.indexOf(String(b.dataset.productId || ""));
			if (ai === -1 && bi === -1) {
				return Number(a.dataset.index || 0) - Number(b.dataset.index || 0);
			}
			if (ai === -1) return 1;
			if (bi === -1) return -1;
			return ai - bi;
		});
	}

	async function processImgScope(imgScope, colorHint, token, redetect) {
		if (token !== runToken) return;

		const box = imgScope.closest(".product-markers__box");
		if (box?.classList.contains("product-markers__box--hide-markers")) return;
		if (
			box?.classList.contains("product-markers__box--hide-markers-mobile") &&
			window.matchMedia("(max-width: 989px)").matches
		) {
			return;
		}

		const overlay = imgScope.querySelector(
			".product-markers__overlay[data-dot-scan-src]",
		);
		if (!overlay) return;

		const root = imgScope.closest(".product-markers");
		const scanSrc = overlay.dataset.dotScanSrc;
		if (!scanSrc) return;

		const colorInput =
			overlay.dataset.dotTargetColor ||
			colorHint ||
			(root && root.dataset.markerDotColor) ||
			"#FF00FF";

		const imgBox = imgScope.querySelector(".product-markers__img-box");
		const displayImg = imgScope.querySelector("img.product-markers__room-image");
		if (!imgBox || !displayImg) return;

		const markers = sortMarkersByCollectionOrder(
			Array.from(
				imgBox.querySelectorAll(
					":scope > .js-product-markers__item.product-markers__marker",
				),
			).filter((m) => m.querySelector(".markers-card")),
			overlay,
		);

		if (!markers.length) return;

		await waitForStableLayout(imgBox, displayImg);
		if (token !== runToken) return;

		await new Promise((r) =>
			requestAnimationFrame(() => requestAnimationFrame(r)),
		);
		if (token !== runToken) return;

		let naturalClusters = !redetect
			? readCachedNaturalClusters(overlay)
			: null;

		if (!naturalClusters || !naturalClusters.length) {
			const rgb = parseTargetRgb(colorInput);
			naturalClusters = await detectNaturalClusters(
				scanSrc,
				rgb,
				markers.length,
			);
			if (token !== runToken) return;
			if (!naturalClusters.length) return;
			writeCachedNaturalClusters(overlay, naturalClusters);
		}

		const imgR = displayImg.getBoundingClientRect();
		const boxR = imgBox.getBoundingClientRect();
		if (!imgR.width || !imgR.height || !boxR.width || !boxR.height) return;

		const nw = displayImg.naturalWidth;
		const nh = displayImg.naturalHeight;
		if (!nw || !nh) return;

		const fineOffsetX = Number(root?.dataset.markerDotOffsetX || 0);
		const fineOffsetY = Number(root?.dataset.markerDotOffsetY || 0);

		for (let i = 0; i < markers.length && i < naturalClusters.length; i++) {
			const nx = naturalClusters[i].rx * nw;
			const ny = naturalClusters[i].ry * nh;
			const { x, y } = naturalToDisplayPoint(
				nx,
				ny,
				nw,
				nh,
				imgR.width,
				imgR.height,
				displayImg,
			);
			const pxX = imgR.left - boxR.left + x;
			const pxY = imgR.top - boxR.top + y;
			markers[i].dataset.dotPctX = ((pxX / boxR.width) * 100).toFixed(2);
			markers[i].dataset.dotPctY = ((pxY / boxR.height) * 100).toFixed(2);
			applyPinDotCentered(
				markers[i],
				pxX,
				pxY,
				imgBox,
				fineOffsetX,
				fineOffsetY,
			);
		}

		if (root) {
			root.dataset.dotClustersUsed = String(
				Math.min(markers.length, naturalClusters.length),
			);
		}
	}

	function revealMarkers(root) {
		if (!root) return;
		const pendingScopes = root.querySelectorAll(
			".product-markers__img--stage-pending",
		);
		if (pendingScopes.length) return;
		root.classList.remove("product-markers--dot-pending");
		root.classList.add("product-markers--dots-ready");
	}

	function signalScopeReady(scope) {
		if (!scope) return;
		scope.dataset.scopeMarkersReady = "true";
		scope.dispatchEvent(
			new CustomEvent("product-markers:scope-ready", { bubbles: true }),
		);
	}

	async function runAll(redetect) {
		const token = ++runToken;
		const roots = document.querySelectorAll(
			'.product-markers[data-marker-dot-detection="true"]',
		);

		for (const root of roots) {
			if (token !== runToken) return;
			const colorHint = root.dataset.markerDotColor;
			const scopes = root.querySelectorAll(".product-markers__img");
			for (const scope of scopes) {
				if (token !== runToken) return;
				delete scope.dataset.scopeMarkersReady;
				try {
					await processImgScope(scope, colorHint, token, redetect);
				} finally {
					if (token === runToken) {
						signalScopeReady(scope);
						revealMarkers(root);
					}
				}
			}
		}
	}

	function scheduleRun(redetect) {
		clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => {
			runAll(redetect).catch(() => {});
		}, 250);
	}

	document.addEventListener("DOMContentLoaded", () => {
		const start = () => scheduleRun(true);
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(start);
		} else {
			start();
		}
	});

	window.addEventListener("load", () => scheduleRun(false));
	window.addEventListener("resize", () => scheduleRun(false));

	document.addEventListener("shopify:section:load", (event) => {
		const root = event.target?.querySelector?.(
			'.product-markers[data-marker-dot-detection="true"]',
		);
		if (root) {
			root.classList.add("product-markers--dot-pending");
			root.classList.remove("product-markers--dots-ready");
		}
		scheduleRun(true);
	});
})();
